import type {
  Access,
  SectionConfig,
  PermissionValue,
} from '@kizen/permissions/types';
import type { CustomObjectSectionData } from './types';

import { usePermissionsContext } from '@kizen/permissions/hooks/context';
import { useDisableSectionPermissions } from '@kizen/permissions/hooks/useDisableSectionPermissions';
import { usePermissionValue } from '@kizen/permissions/hooks/usePermissionValue';
import { useUpdatePermissionValues } from '@kizen/permissions/hooks/useUpdatePermissionValues';
import { getStreamKey } from '@kizen/permissions/utils';
import { useCallback, useMemo, useRef } from 'react';
import {
  CategoryLabel,
  PermissionRow,
  Section,
  SectionContent,
} from './components';

type RowWithTooltipProps = {
  affordance: 'checkbox' | 'range';
  allowedAccess?: Access[];
  label: string;
  labelIndent?: number;
  permission: string;
  section: string;
  tooltip?: string;
};

type AppAreaSectionProps = {
  section: SectionConfig;
};

const RowWithTooltip = ({
  section,
  permission,
  affordance,
  label,
  allowedAccess = [],
  ...rest
}: RowWithTooltipProps) => {
  const tooltip = usePermissionValue(
    getStreamKey(permission, section),
    ({ tooltip }) => tooltip
  );
  const isBooleanPermission = usePermissionValue(
    getStreamKey(permission, section),
    ({ initial }) => typeof initial?.value === 'boolean'
  );

  const disabledSteps = useMemo(() => {
    return isBooleanPermission
      ? []
      : Object.entries({
          view: 1,
          edit: 2,
          remove: 3,
        }).reduce<number[]>((collect, [key, value]) => {
          if (allowedAccess.includes(key as Access)) {
            return collect;
          }
          return [...collect, value];
        }, []);
  }, [allowedAccess, isBooleanPermission]);

  return (
    <PermissionRow
      section={section}
      permission={permission}
      affordance={affordance}
      label={label}
      tooltip={tooltip}
      disabledSteps={disabledSteps}
      {...rest}
    />
  );
};

export const AppAreaSection = ({ section }: AppAreaSectionProps) => {
  const { key, label, permissions, categories } = section;
  const ctx = usePermissionsContext();
  const {
    meta_config: { default_access_labels },
  } = ctx;
  const previousCategoryHadLabels = useRef(false);
  const isSectionEnabled = usePermissionValue(key, ({ value }) => value);
  const updatePermissionValues = useUpdatePermissionValues();
  const disableSectionPermissions = useDisableSectionPermissions();

  const sectionLabels = section.access_labels ?? default_access_labels;
  const hasPermissions = permissions && permissions.length > 0;
  const hasCategories = categories && categories?.length > 0;

  const handleSectionToggle = useCallback(
    (value: boolean) => {
      if (permissions) {
        value
          ? updatePermissionValues(
              permissions.reduce<Record<string, PermissionValue>>(
                (acc, config) => {
                  acc[getStreamKey(config.key, key)] = config.default;
                  return acc;
                },
                {}
              )
            )
          : disableSectionPermissions(key);
      }
    },
    [key, permissions, updatePermissionValues, disableSectionPermissions]
  );

  const categorizedPermissions = useMemo(() => {
    return hasPermissions
      ? permissions.reduce<CustomObjectSectionData['main']>(
          (acc, permission) => {
            if (
              hasCategories &&
              permission.category &&
              !acc.find(({ key }) => key === permission.category)
            ) {
              const category = categories.find(
                ({ key }) => key === permission.category
              );
              if (category) acc.push({ ...category, isCategory: true });
            }
            acc.push(permission);
            return acc;
          },
          []
        )
      : [];
  }, [permissions, categories, hasCategories, hasPermissions]);

  return (
    <Section
      showExpander={hasPermissions}
      title={label}
      sectionId={key}
      onChange={handleSectionToggle}
    >
      {isSectionEnabled && hasPermissions && (
        <SectionContent labels={sectionLabels}>
          {categorizedPermissions.map((data) => {
            if ('isCategory' in data) {
              const {
                key: permission_key,
                label,
                tooltip,
                access_labels,
              } = data;

              let categoryLabels = access_labels;

              if (previousCategoryHadLabels.current && !categoryLabels) {
                categoryLabels = sectionLabels;
              }

              previousCategoryHadLabels.current = Boolean(categoryLabels);

              return (
                <CategoryLabel
                  key={permission_key}
                  marginBottom={label ? null : "17"}
                  accessLabels={categoryLabels}
                  label={label}
                  tooltip={tooltip}
                />
              );
            }

            const {
              affordance,
              key: permission_key,
              label,
              category,
              allowed_access,
            } = data;

            return (
              <RowWithTooltip
                key={permission_key}
                section={key}
                permission={permission_key}
                affordance={affordance}
                label={label}
                labelIndent={category ? 25 : 0}
                allowedAccess={allowed_access}
              />
            );
          })}
        </SectionContent>
      )}
    </Section>
  );
};
